import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtPrice, fmtUSD } from "../lib/api";
import { useAudio } from "../context/AudioContext";
import { useAuth } from "../context/AuthContext";
import { Play, Pause, ShoppingBag, Check, Loader2, ArrowLeft, LogIn, Tag, X, Download, Clock3, Building2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../components/SEO";

const youtubeEmbedUrl = (value) => {
  if (!value) return "";
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const youtubeHosts = new Set([
      "youtube.com",
      "www.youtube.com",
      "m.youtube.com",
      "music.youtube.com",
      "youtube-nocookie.com",
      "www.youtube-nocookie.com",
      "youtu.be",
      "www.youtu.be",
    ]);
    if (!youtubeHosts.has(host) || !["http:", "https:"].includes(url.protocol)) return "";

    const parts = url.pathname.split("/").filter(Boolean);
    let videoId = "";
    if (host === "youtu.be" || host === "www.youtu.be") {
      videoId = parts[0] || "";
    } else if (url.pathname === "/watch") {
      videoId = url.searchParams.get("v") || "";
    } else if (["embed", "shorts", "live"].includes(parts[0])) {
      videoId = parts[1] || "";
    }
    if (!/^[A-Za-z0-9_-]{11}$/.test(videoId)) return "";
    return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;
  } catch {
    return "";
  }
};

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMt, setLoadingMt] = useState(false);
  const [loadingDoku, setLoadingDoku] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({
    doku_enabled: false,
    midtrans_enabled: false,
    paypal_enabled: true,
  });
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount, final_amount}
  const [upgradePricing, setUpgradePricing] = useState(null);
  const [checkoutQuote, setCheckoutQuote] = useState(null);
  const [currencyPreference, setCurrencyPreference] = useState(
    () => localStorage.getItem("ts_display_currency") || "",
  );
  const [couponLoading, setCouponLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("windows");
  const { current, playing, playTrack } = useAudio();
  const { isCustomer } = useAuth();

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => {
        const loadedProduct = r.data;
        setProduct(loadedProduct);
        const availablePlatforms = loadedProduct.available_platforms || [];
        const windowsAvailable = availablePlatforms.includes("windows");
        const macosAvailable = availablePlatforms.includes("macos");
        const productAvailable = availablePlatforms.includes("product");
        setSelectedPlatform(
          productAvailable
            ? "product"
            : windowsAvailable
              ? "windows"
              : macosAvailable
                ? "macos"
                : "windows",
        );
      })
      .catch(() => nav("/shop"));
  }, [id, nav]);

  useEffect(() => {
    api
      .get("/checkout/payment-methods")
      .then((response) => setPaymentMethods(response.data))
      .catch(() => {
        setPaymentMethods({
          doku_enabled: false,
          midtrans_enabled: false,
          paypal_enabled: true,
        });
      });
  }, []);

  useEffect(() => {
    if (!id) return;
    setCheckoutQuote(null);
    const params = new URLSearchParams();
    if (couponApplied?.code && couponApplied?.coupon_type !== "trial") {
      params.set("coupon_code", couponApplied.code);
    }
    if (currencyPreference) params.set("currency", currencyPreference);
    const query = params.toString();
    api
      .get(`/checkout/quote/${id}${query ? `?${query}` : ""}`)
      .then((response) => {
        setCheckoutQuote(response.data);
        setUpgradePricing(
          response.data.discount_source === "owner_upgrade" ? response.data : null,
        );
      })
      .catch(() => {
        setCheckoutQuote(null);
        setUpgradePricing(null);
      });
  }, [id, isCustomer, currencyPreference, couponApplied?.code, couponApplied?.coupon_type]);

  const chooseCurrency = (currency) => {
    setCheckoutQuote(null);
    setCurrencyPreference(currency);
    if (currency) localStorage.setItem("ts_display_currency", currency);
    else localStorage.removeItem("ts_display_currency");
  };

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponLoading(true);
    try {
      const r = await api.post("/checkout/apply-coupon", { code, product_id: id });
      setCheckoutQuote(null);
      setCouponApplied(r.data);
      toast.success(
        r.data.coupon_type === "trial"
          ? `${r.data.trial_days}-day trial coupon applied`
          : r.data.discount_source === "owner_upgrade"
            ? "Coupon is valid, but the owner discount is better"
            : `Coupon applied: -${fmtPrice(r.data.discount)}`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid coupon");
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCheckoutQuote(null);
    setCouponApplied(null);
    setCouponInput("");
  };

  const claimFree = async () => {
    if (!isCustomer) {
      toast.info("Please sign in to claim this free product");
      nav("/login", { state: { from: `/shop/${id}` } });
      return;
    }
    setLoading(true);
    try {
      const endpoint = product?.is_free ? `/free-claim/${id}` : `/coupon-claim/${id}`;
      const r = await api.post(endpoint, {
        platform: selectedPlatform,
        ...(!product?.is_free && { coupon_code: couponApplied?.code || "" }),
      });
      toast.success(r.data.already_claimed ? "You already own this product" : "Added to your library!");
      nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to claim product");
      setLoading(false);
    }
  };
  

  const checkout = async () => {
    if (product?.is_free || (couponApplied && Number(couponApplied.final_amount) <= 0)) {
      return claimFree();
    }
    if (!isCustomer) {
      toast.info("Please sign in to purchase");
      nav("/login", { state: { from: `/shop/${id}` } });
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        product_id: id,
        origin_url: window.location.origin,
        coupon_code: couponApplied?.code || "",
        platform: selectedPlatform,
        quote_token: checkoutQuote?.quote_token || "",
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setLoading(false);
    }
  };


  const payPaypal = async () => {
  if (!isCustomer) {
    toast.info("Please sign in to purchase");
    nav("/login", { state: { from: `/shop/${id}` } });
    return;
  }

  setLoading(true);

  try {
    const r = await api.post("/checkout/session", {
      product_id: id,
      origin_url: window.location.origin,
      coupon_code: couponApplied?.code || "",
      platform: selectedPlatform,
      quote_token: checkoutQuote?.quote_token || "",
    });

    window.location.href = r.data.url;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Failed to start PayPal checkout");
    setLoading(false);
  }
};

  const payDoku = async () => {
    if (!isCustomer) {
      toast.info("Please sign in to purchase");
      nav("/login", { state: { from: `/shop/${id}` } });
      return;
    }
    setLoadingDoku(true);
    try {
      const response = await api.post("/checkout/doku/session", {
        product_id: id,
        origin_url: window.location.origin,
        coupon_code: couponApplied?.code || "",
        platform: selectedPlatform,
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to start DOKU payment");
      setLoadingDoku(false);
    }
  };


 const startTrial = async () => {
  if (trialLoading) return;

  if (!isCustomer) {
    toast.info("Please sign in to start your trial");
    nav("/login", { state: { from: `/shop/${id}` } });
    return;
  }

  setTrialLoading(true);

  try {
    const token = localStorage.getItem("ts_token");

    // 🔥 REQUEST PERTAMA (PAKSA BAWA TOKEN)
    let res;
    try {
      res = await api.post(`/customer/trials/${id}`, {
        platform: selectedPlatform,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      // 🔥 JIKA 401 → RETRY SEKALI (INI KUNCI FIX KLIK 2x)
      if (err?.response?.status === 401) {
        console.warn("Retry trial after 401...");

        res = await api.post(`/customer/trials/${id}`, {
          platform: selectedPlatform,
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("ts_token")}`,
          },
        });
      } else if (err?.response) {
        res = err.response;
      } else {
        throw err;
      }
    }

    const downloadUrl = res?.data?.download_url;

    if (!downloadUrl) {
      console.error("TRIAL RESPONSE:", res?.data);
      toast.error("Download URL not found");
      return;
    }

    // ✅ LANGSUNG DOWNLOAD
    window.location.replace(downloadUrl);

  } catch (e) {
    console.error("TRIAL ERROR:", e);

    toast.error(
      e?.response?.data?.detail ||
      "Failed to create trial"
    );
  } finally {
    setTrialLoading(false);
  }
};

  const startCouponTrial = async () => {
    if (trialLoading) return;
    if (!isCustomer) {
      toast.info("Please sign in to activate the trial");
      nav("/login", { state: { from: `/shop/${id}` } });
      return;
    }

    setTrialLoading(true);
    try {
      const response = await api.post(`/customer/trial-coupons/${id}`, {
        platform: selectedPlatform,
        coupon_code: couponApplied?.code || "",
      });
      toast.success(
        response.data.already_created
          ? "Trial ini sudah aktif"
          : `${couponApplied?.trial_days || 1}-day trial license created`,
      );
      if (response.data.download_url) {
        window.location.replace(response.data.download_url);
      } else {
        nav("/dashboard");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to activate trial coupon");
    } finally {
      setTrialLoading(false);
    }
  };

  const loadSnap = (clientKey, isProduction) =>
    new Promise((resolve, reject) => {
      if (window.snap) return resolve();
      const src = isProduction
        ? "https://app.midtrans.com/snap/snap.js"
        : "https://app.sandbox.midtrans.com/snap/snap.js";
      const existing = document.getElementById("midtrans-snap");
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("snap load error")));
        return;
      }
      const s = document.createElement("script");
      s.id = "midtrans-snap";
      s.src = src;
      s.setAttribute("data-client-key", clientKey || "");
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("Failed to load Midtrans"));
      document.body.appendChild(s);
    });

  const payMidtrans = async () => {
    if (!isCustomer) {
      toast.info("Please sign in to purchase");
      nav("/login", { state: { from: `/shop/${id}` } });
      return;
    }
    setLoadingMt(true);
    try {
      const r = await api.post("/checkout/midtrans/session", {
        product_id: id,
        origin_url: window.location.origin,
        coupon_code: couponApplied?.code || "",
        platform: selectedPlatform,
      });
      await loadSnap(r.data.client_key, r.data.is_production);
      if (!window.snap) {
        window.location.href = r.data.redirect_url;
        return;
      }
      window.snap.pay(r.data.token, {
        onSuccess: () => nav(`/payment/success?order_id=${r.data.order_id}`),
        onPending: () => nav(`/payment/success?order_id=${r.data.order_id}`),
        onError: () => {
          toast.error("Payment failed");
          setLoadingMt(false);
        },
        onClose: () => setLoadingMt(false),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Midtrans payment");
      setLoadingMt(false);
    }
  };

  if (!product) {
    return <div className="pt-40 text-center text-zinc-500">Loading...</div>;
  }

  const couponIsTrial = couponApplied?.coupon_type === "trial";
  const activePricing = !couponIsTrial && couponApplied
    ? couponApplied
    : upgradePricing;
  const displayCurrency = checkoutQuote?.display_currency || currencyPreference || "IDR";
  const fxRate = Number(checkoutQuote?.fx_rate || 0);
  const formatLocalized = (amountIdr) =>
    displayCurrency === "USD" && fxRate > 0
      ? fmtUSD(Number(amountIdr || 0) / fxRate)
      : fmtPrice(amountIdr);
  const paypalAmount = checkoutQuote?.amounts?.USD?.final_amount;
  const paypalPrice = Number.isFinite(Number(paypalAmount))
    ? fmtUSD(paypalAmount)
    : "USD";
  const hasDiscount = Number(activePricing?.discount || 0) > 0;
  const ownerDiscount = activePricing?.discount_source === "owner_upgrade";
  const couponMakesFree =
    Boolean(couponApplied) && Number(couponApplied.final_amount) <= 0;
  const isFreeCheckout = product.is_free || couponMakesFree;

  const paymentLabels = [
    paymentMethods.doku_enabled ? "DOKU" : null,
    paymentMethods.midtrans_enabled ? "Midtrans" : null,
    paymentMethods.paypal_enabled ? "PayPal" : null,
  ].filter(Boolean);

  const isCurrent = current?.id === `preview-${product.id}`;
  const previewTrack = product.preview_audio_url
    ? {
        id: `preview-${product.id}`,
        title: product.name,
        artist: "Preview",
        audio_url: product.preview_audio_url,
        cover_url: product.image_url,
      }
    : null;
  const youtubePreviewUrl = youtubeEmbedUrl(product.preview_youtube_url);

  return (
    <>
      <SEO
        title={product.name}
        description={product.description}
        path={`/shop/${product.id}`}
        image={product.image_url}
        type="product"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description,
          image: product.image_url ? [product.image_url] : undefined,
          sku: product.id,
          category: product.category,
          brand: {
            "@type": "Brand",
            name: "TripleSide Studio",
          },
          offers: {
            "@type": "Offer",
            url: `https://triplesidestudio.com/shop/${product.id}`,
            priceCurrency: "IDR",
            price: String(product.is_free ? 0 : product.price),
            availability: "https://schema.org/InStock",
          },
        }}
      />
      <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-32">
      <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to Shop
      </Link>

      <div className="grid md:grid-cols-2 gap-12 fade-up">
        <div className="md:sticky md:top-28 md:self-start">
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0c]">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {youtubePreviewUrl && (
            <div
              data-testid="product-youtube-preview"
              className="mt-6 aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black"
            >
              <iframe
                src={youtubePreviewUrl}
                title={`${product.name} YouTube preview`}
                className="h-full w-full"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          )}
          {previewTrack && (
            <button
              data-testid="preview-play-btn"
              onClick={() => playTrack(previewTrack)}
              className="mt-6 w-full flex items-center justify-center gap-3 py-4 rounded-full border border-white/15 hover:bg-white/5 transition-colors font-semibold"
            >
              {isCurrent && playing ? <Pause className="w-4 h-4" fill="white" /> : <Play className="w-4 h-4" fill="white" />}
              Preview Audio
            </button>
          )}
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="text-[10px] font-mono text-[#e11d48] uppercase tracking-[0.3em]">{product.category}</div>
            {couponIsTrial ? (
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500/15 text-sky-400 border border-sky-500/30">
                Trial {couponApplied.trial_days} Days
              </span>
            ) : isFreeCheckout && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                Free
              </span>
            )}
          </div>
          <h1 className="font-[Outfit] text-4xl md:text-6xl font-black tracking-tighter mb-4">{product.name}</h1>
          {product.owner_engineer_name && <p className="-mt-2 mb-4 text-sm text-zinc-400">Sold by <span className="font-semibold text-white">{product.owner_engineer_name}</span> · Verified Engineer</p>}
          {!product.is_free && Number(product.price || 0) > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs font-mono">
              <span className="text-zinc-500">Currency:</span>
              {[
                ["", "Auto"],
                ["IDR", "IDR"],
                ["USD", "USD"],
              ].map(([value, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => chooseCurrency(value)}
                  className={`rounded-full border px-3 py-1 transition-colors ${
                    currencyPreference === value
                      ? "border-[#e11d48] bg-[#e11d48]/15 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/25"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {couponIsTrial ? (
            <div className="mb-8" data-testid="product-price">
              <div className="text-sm text-zinc-500 font-mono">
                Full license: {formatLocalized(product.price)}
              </div>
              <div className="font-[Outfit] text-5xl font-black text-sky-400">
                Free Trial
              </div>
              <div className="text-xs font-mono text-sky-300 mt-1">
                License active for {couponApplied.trial_days} days
              </div>
            </div>
          ) : isFreeCheckout ? (
            <div className="mb-8" data-testid="product-price">
              {couponMakesFree && (
                <div className="text-2xl line-through text-zinc-500 font-[Outfit] font-bold">
                  {formatLocalized(couponApplied.original_amount)}
                </div>
              )}
              <div className="font-[Outfit] text-5xl font-black text-emerald-400">
                Free
              </div>
              {couponMakesFree && (
                <div className="text-xs font-mono text-emerald-400 mt-1">
                  Coupon {couponApplied.code} covers the full price
                </div>
              )}
            </div>
          ) : hasDiscount ? (
            <div className="mb-8" data-testid="product-price">
              <div className="text-2xl line-through text-zinc-500 font-[Outfit] font-bold">
                {formatLocalized(activePricing.original_amount)}
              </div>
              <div className="font-[Outfit] text-5xl font-black text-[#e11d48]">
                {formatLocalized(activePricing.final_amount)}
              </div>
              <div className="text-xs font-mono text-emerald-400 mt-1">
                {ownerDiscount
                  ? `Owner upgrade from ${activePricing.upgrade_from_product_name || "a previous product"} · Save ${formatLocalized(activePricing.discount)}`
                  : `Save ${formatLocalized(activePricing.discount)} with ${activePricing.code}`}
              </div>
            </div>
          ) : (
            <div className="font-[Outfit] text-5xl font-black text-[#e11d48] mb-8" data-testid="product-price">
              {formatLocalized(product.price)}
            </div>
          )}

          {displayCurrency === "USD" && checkoutQuote?.fx_rate && !isFreeCheckout && (
            <div className="-mt-6 mb-8 text-xs font-mono text-zinc-500">
              PayPal price based on 1 USD = {fmtPrice(checkoutQuote.fx_rate)}
            </div>
          )}

          <p className="text-zinc-300 leading-relaxed mb-8 text-lg">{product.description}</p>
          {product.license_name && <div className="mb-8 rounded-2xl border border-white/10 bg-white/[0.03] p-4"><div className="text-[10px] uppercase tracking-widest text-[#fb7185]">Product license</div><div className="mt-2 text-sm font-semibold">{product.license_name}</div>{product.license_terms && <p className="mt-2 whitespace-pre-wrap text-xs leading-6 text-zinc-400">{product.license_terms}</p>}</div>}

          {product.requires_license && (
            <div className="mb-6 rounded-xl border border-white/10 bg-[#0a0a0c] p-4 text-sm text-zinc-300">
              <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-400" /> One serial, usable on up to <strong className="text-white">{product.max_activations || 1} computer(s)</strong>.</div>
              {(couponIsTrial || product.trial_enabled !== false) && <div className="mt-2 flex items-center gap-2"><Clock3 className="h-4 w-4 text-[#fb7185]" /> Try every feature free for <strong className="text-white">{couponIsTrial ? couponApplied.trial_days : product.trial_days || 7} days</strong>.</div>}
            </div>
          )}

          <div className="space-y-3 mb-8">
            {(product.asset_type === "vst_business_plan"
              ? ["Hak build aktif otomatis setelah pembayaran lunas", `${product.business_plan_name || "Paket VST"} sekali bayar`, "Lisensi dan kuota build sudah termasuk"]
              : ["Instant download after purchase", "Royalty-free for personal and commercial use", "Email receipt and PDF invoice"]
            ).map((f) => (
              <div key={f} className="flex items-center gap-3 text-sm text-zinc-300">
                <div className="w-6 h-6 rounded-full bg-[#e11d48]/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-[#e11d48]" />
                </div>
                {f}
              </div>
            ))}
          </div>

          {!couponIsTrial && product.requires_license && product.trial_enabled !== false && isCustomer && (
            <button
              data-testid="start-trial-btn"
              onClick={startTrial}
              disabled={trialLoading}
              className="mb-6 w-full flex items-center justify-center gap-2 py-3 rounded-full border border-[#e11d48]/50 text-[#fb7185] hover:bg-[#e11d48]/10 font-semibold disabled:opacity-60"
            >
              {trialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock3 className="w-4 h-4" />}
              Try Free for {product.trial_days || 7} Days
            </button>
          )}

          {/* Coupon (hidden for free products) */}
          {!product.is_free && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Coupon Code</span>
              </div>
              {couponApplied ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                  <Tag className="w-4 h-4 text-emerald-400" />
                  <span className="font-mono font-bold text-emerald-400">{couponApplied.code}</span>
                  <span className="text-xs text-zinc-400 flex-1">
                    {couponIsTrial
                      ? `${couponApplied.trial_days}-day trial`
                      : couponApplied.discount_source === "owner_upgrade"
                        ? "valid · owner discount is better"
                        : "applied"}
                  </span>
                  <button
                    data-testid="coupon-remove"
                    onClick={removeCoupon}
                    className="p-1 rounded-full hover:bg-white/5 text-zinc-400"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="coupon-input"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 bg-[#050505] border border-white/10 rounded-full px-4 py-2.5 text-sm font-mono uppercase focus:outline-none focus:border-[#e11d48]"
                  />
                  <button
                    data-testid="coupon-apply"
                    onClick={applyCoupon}
                    disabled={couponLoading || !couponInput.trim()}
                    className="px-5 py-2.5 rounded-full border border-white/15 text-sm font-semibold hover:bg-white/5 disabled:opacity-50"
                  >
                    {couponLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apply"}
                  </button>
                </div>
              )}
            </div>
          )}

          {couponIsTrial ? (
  <button
    data-testid="start-coupon-trial-btn"
    onClick={startCouponTrial}
    disabled={trialLoading}
    className="w-full md:w-auto px-10 py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-60 bg-sky-500 hover:bg-sky-600 text-white"
  >
    {trialLoading ? (
      <><Loader2 className="w-4 h-4 animate-spin" /> Creating trial license...</>
    ) : isCustomer ? (
      <><Clock3 className="w-4 h-4" /> Activate {couponApplied.trial_days}-Day Trial</>
    ) : (
      <><LogIn className="w-4 h-4" /> Sign In to Activate Trial</>
    )}
  </button>
) : isFreeCheckout ? (
  <button
    data-testid="buy-now-btn"
    onClick={checkout}
    disabled={loading}
    className="w-full md:w-auto px-10 py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-60 bg-emerald-500 hover:bg-emerald-600 text-white"
  >
    {loading ? (
      <><Loader2 className="w-4 h-4 animate-spin" /> Working...</>
    ) : isCustomer ? (
      <><Download className="w-4 h-4" /> Get for Free</>
    ) : (
      <><LogIn className="w-4 h-4" /> Sign In to Get It Free</>
    )}
  </button>
) : (
  <div className="space-y-3 max-w-md">
    {paymentMethods.doku_enabled && (
      <button
        data-testid="pay-doku-btn"
        onClick={payDoku}
        disabled={loadingDoku || loading || loadingMt}
        className="w-full px-10 py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-60 bg-[#e11d48] hover:bg-[#be123c] glow-brand"
      >
        {loadingDoku ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Creating order...</>
        ) : isCustomer ? (
          <><Building2 className="w-4 h-4" /> Pay with DOKU</>
        ) : (
          <><LogIn className="w-4 h-4" /> Sign In to Purchase</>
        )}
      </button>
    )}

    {paymentMethods.midtrans_enabled && (
      <button
        data-testid="pay-midtrans-btn"
        onClick={payMidtrans}
        disabled={loadingMt || loading || loadingDoku}
        className="w-full px-10 py-4 rounded-full font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-60 bg-[#e11d48] hover:bg-[#be123c] glow-brand"
      >
        {loadingMt ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Opening secure checkout...</>
        ) : isCustomer ? (
          <><ShoppingBag className="w-4 h-4" /> Pay with Midtrans</>
        ) : (
          <><LogIn className="w-4 h-4" /> Sign In to Purchase</>
        )}
      </button>
    )}

    {paymentMethods.paypal_enabled && isCustomer && (
      <button
        data-testid="pay-paypal-btn"
        onClick={payPaypal}
        disabled={loading || loadingMt || loadingDoku || !checkoutQuote}
        className="w-full px-10 py-3.5 rounded-full font-semibold transition-colors flex items-center justify-center gap-3 disabled:opacity-60 border border-white/15 hover:bg-white/5 text-zinc-200"
      >
        {loading || !checkoutQuote ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {loading ? "Working..." : "Updating price..."}
          </>
        ) : (
          <>
            <ShoppingBag className="w-4 h-4" />
            Pay with PayPal · {paypalPrice}
          </>
        )}
      </button>
    )}
  </div>
)}

<p className="text-xs text-zinc-500 mt-4 font-mono">
            {couponIsTrial
              ? `Coupon ${couponApplied.code} provides a ${couponApplied.trial_days}-day trial license.`
              : isFreeCheckout
              ? couponMakesFree
                ? `Free with coupon ${couponApplied.code} · Sign in to add it to your library.`
                : "100% free · Sign in to add it to your library."
              : isCustomer
              ? `${ownerDiscount ? "Upgrade discount applied automatically · " : ""}Payment methods: ${paymentLabels.join(" or ")}`
              : "You need an account to purchase digital products."}
          </p>
        </div>
      </div>
      </div>
    </>
  );
}
